export type FilterOptionPersisted = {
  id: string;
};

/**
 * Filter context identifies which page/feature the filters belong to
 */
export enum FilterContext {
  /** Main displays overview page */
  DISPLAYS = 'displays',
  /** Claim code history page */
  CLAIM_CODES = 'claimCodes',
  /** Configuration assigned displays page */
  CONFIGURATION = 'configuration',
  /** Display under claim codes page */
  CLAIM_CODES_DISPLAY = 'claimCodeDisplays',
}

/**
 * Map of filter column IDs to their persisted values
 * e.g., { "site": [{label: "Site A", value: "id1"}], "groups": [...] }
 */
export type CustomerFiltersMap = Record<string, FilterOptionPersisted[]>;

/**
 * Filters for the claim-code-displays context, keyed by claim code ID.
 * Each claim code gets its own independent filter bucket.
 * e.g., { "claim-code-id-1": { "groups": [...] }, "claim-code-id-2": { "site": [...] } }
 */
export type ClaimCodeFiltersMap = Record<string, CustomerFiltersMap>;

/**
 * Map of filter contexts to their filter maps, plus metadata
 * Structure under each customerId:
 * {
 *   "_customerHandle": "blr-ic",
 *   "displays": {...},
 *   "claimCodes": {...},
 *   "configuration": {...}
 * }
 *
 * The _customerHandle field stores the current handle for reference/migration:
 * - Customer IDs are stable and never change (used as key)
 * - Customer handles can be changed by users in settings (stored as metadata)
 */
export type ContextFiltersMap = {
  /** Customer handle - stored for reference when handles change */
  customerHandle?: string;
  /** Filters for displays page */
  [FilterContext.DISPLAYS]?: CustomerFiltersMap;
  /** Filters for claim codes page */
  [FilterContext.CLAIM_CODES]?: CustomerFiltersMap;
  /** Filters for configuration page */
  [FilterContext.CONFIGURATION]?: CustomerFiltersMap;
  /** Filters for claim code displays page - keyed by claim code ID */
  [FilterContext.CLAIM_CODES_DISPLAY]?: ClaimCodeFiltersMap;
};

/**
 * Map of customer IDs to their context-based filters
 * e.g., {
 *   "abc123": { "customerHandle": "blr-ic", "displays": {...} }
 * }
 */
export type UserFiltersMap = Record<string, ContextFiltersMap>;

/**
 * Top-level storage structure: hashed user ID -> customer filters
 * e.g., {
 *   "hashed-user-id": {
 *     "abc123": { "_customerHandle": "blr-ic", "displays": {...} }
 *   }
 * }
 */
export type WaveFiltersStorage = Record<string, UserFiltersMap>;

// LocalStorage key
export const STORAGE_KEY = 'wave_filters';
